import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  IconButton,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  DefaultButton,
  TextField,
  Dropdown,
  IDropdownOption,
} from "@fluentui/react";
import {
  getDatabases,
  searchXpressTraxCustomers,
  DatabaseResponse,
  XpressTraxCustomerResponse,
} from "../../api/cortexCloning";
import { renameCompany } from "../../api/renameCompany";

const MAX_COMPANY_NAME_LENGTH = 50;
const CONTROL_CHAR_PATTERN = /[\x00-\x1F\x7F]/;

const validateNewCompanyName = (newName: string, oldName: string): string | null => {
  if (!newName.trim()) return "New company name is required.";
  if (newName.length > MAX_COMPANY_NAME_LENGTH)
    return `Must be ${MAX_COMPANY_NAME_LENGTH} characters or fewer.`;
  if (newName !== newName.trim()) return "No leading or trailing whitespace.";
  if (CONTROL_CHAR_PATTERN.test(newName)) return "No control characters allowed.";
  if (oldName && newName === oldName) return "New name must differ from the current name.";
  return null;
};

const RenameCompanyPage = () => {
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState("");
  const [warningMessage, setWarningMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Company search
  const [companySearchTerm, setCompanySearchTerm] = useState("");
  const [companySearchResults, setCompanySearchResults] = useState<XpressTraxCustomerResponse[]>([]);
  const [loadingCompanySearch, setLoadingCompanySearch] = useState(false);
  const [companySearchError, setCompanySearchError] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [server, setServer] = useState("");

  // Database
  const [databases, setDatabases] = useState<DatabaseResponse[]>([]);
  const [selectedDatabase, setSelectedDatabase] = useState("");
  const [loadingDatabases, setLoadingDatabases] = useState(false);

  // New company name
  const [newCompanyName, setNewCompanyName] = useState("");

  // Debounced XpressTrax company search
  useEffect(() => {
    if (companySearchTerm.length < 2) {
      setCompanySearchResults([]);
      setCompanySearchError("");
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoadingCompanySearch(true);
      setCompanySearchError("");
      try {
        const result = await searchXpressTraxCustomers(companySearchTerm);
        if (!cancelled) setCompanySearchResults(result.customers);
      } catch {
        if (!cancelled) {
          setCompanySearchResults([]);
          setCompanySearchError("Search failed. Check your connection and try again.");
        }
      } finally {
        if (!cancelled) setLoadingCompanySearch(false);
      }
    }, 300);

    return () => { cancelled = true; clearTimeout(timer); };
  }, [companySearchTerm]);

  // Auto-fetch databases when company and server are set
  useEffect(() => {
    const id = parseInt(companyId);
    if (!id || !server) {
      setDatabases([]);
      setSelectedDatabase("");
      return;
    }

    let cancelled = false;
    const fetchDatabases = async () => {
      setLoadingDatabases(true);
      try {
        const data = await getDatabases(id, server);
        if (cancelled) return;
        setDatabases(data);
        setSelectedDatabase("");
      } catch {
        if (cancelled) return;
        setErrorMessage("Failed to look up databases for this company/server");
        setDatabases([]);
      } finally {
        if (!cancelled) setLoadingDatabases(false);
      }
    };

    fetchDatabases();
    return () => { cancelled = true; };
  }, [companyId, server]);

  const selectedDb = useMemo(
    () => databases.find((db) => db.databaseName === selectedDatabase),
    [databases, selectedDatabase]
  );
  const currentCompanyName = selectedDb?.companyName ?? "";

  const databaseOptions: IDropdownOption[] = [...databases]
    .sort((a, b) => a.companyLetter.localeCompare(b.companyLetter))
    .map((db) => ({
      key: db.databaseName,
      text: `${db.companyLetter} - ${db.companyName}`,
    }));

  const clearMessages = () => {
    setErrorMessage("");
    setWarningMessage("");
    setSuccessMessage("");
  };

  const handleClear = () => {
    setCompanyId("");
    setCompanyName("");
    setServer("");
    setDatabases([]);
    setSelectedDatabase("");
    setCompanySearchTerm("");
    setCompanySearchResults([]);
    setCompanySearchError("");
    setNewCompanyName("");
    clearMessages();
  };

  const nameValidationError = validateNewCompanyName(newCompanyName, currentCompanyName);
  const showNameError = newCompanyName.length > 0 && nameValidationError !== null;
  const canSubmit =
    !isSubmitting &&
    !!companyId &&
    !!server &&
    !!selectedDatabase &&
    nameValidationError === null;

  const handleSubmit = async () => {
    if (!companyId || !server || !selectedDatabase) {
      setErrorMessage("Search a company, then select a database before saving.");
      return;
    }
    if (!selectedDb) {
      setErrorMessage("Selected database not found — please re-select");
      return;
    }
    if (nameValidationError) {
      setErrorMessage(nameValidationError);
      return;
    }

    const trimmedName = newCompanyName.trim();
    setIsSubmitting(true);
    clearMessages();
    try {
      const result = await renameCompany(
        server,
        parseInt(companyId),
        selectedDb.companyLetter,
        trimmedName
      );

      const partial = result.status === 207 || result.data.errorMessage !== null;
      if (partial) {
        setWarningMessage(
          `Rename partially completed — ExpLicense updated to "${trimmedName}", but XpressTrax failed: ${
            result.data.errorMessage ?? "Unknown error"
          }. Please retry or contact support.`
        );
      } else {
        setSuccessMessage(
          `Company renamed to "${result.data.expLicense.companyName}".`
        );
        setNewCompanyName("");
      }

      // Refresh the database list so the dropdown shows the new name
      try {
        const refreshed = await getDatabases(parseInt(companyId), server);
        setDatabases(refreshed);
      } catch {
        // Non-fatal — the banner already reflects the outcome.
      }
    } catch (error: any) {
      const detail = error?.response?.data || error?.message || "Unknown error";
      setErrorMessage(
        `Failed to rename company: ${
          typeof detail === "string" ? detail : JSON.stringify(detail)
        }`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="page-header">
        <h2 className="page-title">Rename Company</h2>
      </div>

      <div style={{ padding: "20px 0 20px 24px", maxWidth: 560 }}>
        {/* Company search */}
        <div style={{ position: "relative", marginBottom: companyName ? 4 : 8 }}>
          <TextField
            label="Search Company"
            value={companySearchTerm}
            onChange={(_, value) => setCompanySearchTerm(value || "")}
            placeholder="Type company name to search..."
            iconProps={loadingCompanySearch ? { iconName: "ProgressRingDots" } : { iconName: "Search" }}
          />
          {companySearchError && !loadingCompanySearch && companySearchTerm.length >= 2 && (
            <div
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                backgroundColor: "#fff",
                border: "1px solid #edebe9",
                borderRadius: "0 0 4px 4px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                zIndex: 10,
                padding: "10px 12px",
                fontSize: 13,
                color: "#a4262c",
              }}
              role="alert"
            >
              {companySearchError}
            </div>
          )}
          {companySearchResults.length > 0 && companySearchTerm.length >= 2 && (
            <div
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                maxHeight: 200,
                overflowY: "auto",
                backgroundColor: "#fff",
                border: "1px solid #edebe9",
                borderRadius: "0 0 4px 4px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                zIndex: 10,
              }}
            >
              {companySearchResults.map((c) => (
                <div
                  key={`${c.companyId}-${c.serverId}`}
                  onClick={() => {
                    setCompanyId(c.companyId.toString());
                    setCompanyName(c.companyName);
                    setServer(c.serverId ?? "");
                    setCompanySearchTerm("");
                    setCompanySearchResults([]);
                    clearMessages();
                  }}
                  style={{
                    padding: "8px 12px",
                    cursor: "pointer",
                    fontSize: 13,
                    borderBottom: "1px solid #f3f2f1",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f3f2f1")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#fff")}
                >
                  <div style={{ fontWeight: 600, color: "#323130" }}>{c.companyName}</div>
                  <div style={{ fontSize: 12, color: "#605e5c" }}>
                    ID: {c.companyId} — Server: {c.serverId ?? "N/A"}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Selected company card */}
        {companyName && (
          <div
            style={{
              backgroundColor: "#f3f2f1",
              border: "1px solid #edebe9",
              borderRadius: 4,
              padding: "10px 14px",
              marginBottom: 8,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <div style={{ fontSize: 15, fontWeight: 600, color: "#323130" }}>{companyName}</div>
              <div style={{ fontSize: 12, color: "#605e5c", marginTop: 2 }}>
                ID: {companyId} — Server: {server}
              </div>
            </div>
            <IconButton
              iconProps={{ iconName: "Cancel" }}
              title="Clear selection"
              ariaLabel="Clear selection"
              styles={{ root: { width: 28, height: 28 }, icon: { fontSize: 12, color: "#605e5c" } }}
              onClick={() => {
                setCompanyId("");
                setCompanyName("");
                setServer("");
                setDatabases([]);
                setSelectedDatabase("");
                setNewCompanyName("");
                clearMessages();
              }}
            />
          </div>
        )}

        {/* Database dropdown */}
        <Dropdown
          label="Database"
          options={databaseOptions}
          selectedKey={selectedDatabase}
          onChange={(_, option) => {
            setSelectedDatabase(option?.key as string);
            setNewCompanyName("");
            clearMessages();
          }}
          required
          disabled={databases.length === 0 || loadingDatabases}
          placeholder={
            loadingDatabases
              ? "Loading..."
              : databases.length === 0
              ? "Search and select a company above"
              : "Select a database"
          }
          styles={{ root: { marginBottom: 16 } }}
        />

        {/* New company name */}
        <TextField
          label="New Company Name"
          value={newCompanyName}
          onChange={(_, value) => {
            setNewCompanyName(value || "");
            clearMessages();
          }}
          placeholder={
            currentCompanyName
              ? `Current: ${currentCompanyName}`
              : "Enter the new company name"
          }
          required
          onRenderDescription={() => (
            <span
              style={{
                fontSize: 12,
                color:
                  newCompanyName.length > MAX_COMPANY_NAME_LENGTH
                    ? "#a4262c"
                    : "#605e5c",
              }}
            >
              {newCompanyName.length}/{MAX_COMPANY_NAME_LENGTH}
            </span>
          )}
          errorMessage={showNameError ? nameValidationError ?? undefined : undefined}
          disabled={!selectedDatabase}
          styles={{ root: { marginBottom: 20 } }}
        />

        <div style={{ display: "flex", gap: 8 }}>
          <PrimaryButton
            text="Rename Company"
            onClick={handleSubmit}
            disabled={!canSubmit}
          />
          <DefaultButton text="Clear" onClick={handleClear} />
          <DefaultButton
            iconProps={{ iconName: "ComplianceAudit" }}
            text="Audits"
            onClick={() => navigate("/rename-company/audits")}
            styles={{ root: { marginLeft: "auto" } }}
          />
        </div>

        {successMessage && (
          <div style={{ marginTop: 16 }}>
            <MessageBar
              messageBarType={MessageBarType.success}
              onDismiss={() => setSuccessMessage("")}
            >
              {successMessage}
            </MessageBar>
          </div>
        )}

        {warningMessage && (
          <div style={{ marginTop: 16 }}>
            <MessageBar
              messageBarType={MessageBarType.warning}
              onDismiss={() => setWarningMessage("")}
            >
              {warningMessage}
            </MessageBar>
          </div>
        )}
      </div>

      {errorMessage && (
        <MessageBar
          messageBarType={MessageBarType.error}
          onDismiss={() => setErrorMessage("")}
          styles={{ root: { position: "fixed", bottom: 0, left: 0, width: "100%", zIndex: 1000 } }}
        >
          {errorMessage}
        </MessageBar>
      )}
    </>
  );
};

export default RenameCompanyPage;
